import { useEffect, useRef, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { allegati as apiAllegati } from '../api/scadenziario.ts';
import type { Allegato, RegoleAllegati } from '../api/tipi-dati.ts';
import { istante } from './formato.ts';

/**
 * I certificati appesi a una scadenza: elenco, caricamento, cancellazione.
 *
 * Sta dentro il dialogo di dettaglio (punto 2c) e non in una schermata a sé,
 * perché è lì che uno va a cercare il certificato di una scadenza. La scheda
 * mostra i dati **in sola lettura**; gli allegati invece si possono aggiungere
 * e togliere, ed è voluto: se non si potesse da qui, l'unico posto per
 * allegare un file resterebbe il form di modifica, cioè esattamente la strada
 * che il punto 2c esiste per non far più percorrere.
 */
export function PannelloAllegati({ scadenzaId }: { scadenzaId: number }) {
  const [elenco, setElenco] = useState<Allegato[] | null>(null);
  const [regole, setRegole] = useState<RegoleAllegati | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [inInvio, setInInvio] = useState(false);
  /** L'id in corso di cancellazione: disabilita solo la sua riga, non tutte. */
  const [inCancellazione, setInCancellazione] = useState<number | null>(null);

  const campoFile = useRef<HTMLInputElement>(null);

  /** Il tetto di numero è già stato raggiunto? `0` significa nessun tetto. */
  const pieno =
    regole !== null &&
    regole.maxPerScadenza > 0 &&
    (elenco?.length ?? 0) >= regole.maxPerScadenza;

  useEffect(() => {
    let annullato = false;

    Promise.all([apiAllegati.elenca(scadenzaId), apiAllegati.regole()])
      .then(([lista, r]) => {
        if (annullato) return;
        setElenco(lista.elementi);
        setRegole(r);
      })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [scadenzaId]);

  async function carica(file: File) {
    setErrore(null);

    /*
     * Il controllo della dimensione si fa **prima** di partire, con il numero
     * che dice il backend.
     *
     * Non è una guardia di sicurezza — quella sta sul server e resta lì — è
     * cortesia: senza, su una linea lenta si aspetta il caricamento completo
     * di un file da 30 MB per sentirsi dire che era troppo grande. E il
     * caricamento non si sarebbe nemmeno concluso, perché il server chiude il
     * flusso appena supera il limite.
     */
    if (regole !== null && file.size > regole.massimoByte) {
      setErrore(
        `«${file.name}» supera il limite di ${regole.massimoLeggibile} e non è stato caricato.`,
      );
      return;
    }

    setInInvio(true);

    try {
      const nuovo = await apiAllegati.carica(scadenzaId, file);
      setElenco((attuali) => [nuovo, ...(attuali ?? [])]);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInInvio(false);
      // Si svuota il campo, o ricaricare lo stesso file non emetterebbe
      // nessun evento e sembrerebbe che il pulsante non funzioni.
      if (campoFile.current !== null) campoFile.current.value = '';
    }
  }

  async function elimina(allegato: Allegato) {
    setErrore(null);
    setInCancellazione(allegato.id);

    try {
      await apiAllegati.elimina(allegato.id);
      setElenco((attuali) => (attuali ?? []).filter((a) => a.id !== allegato.id));
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      setInCancellazione(null);
    }
  }

  return (
    <section className="allegati">
      <h3 className="sezione-titolo">
        Allegati
        {elenco !== null && elenco.length > 0 && (
          <span className="nota">
            {elenco.length === 1 ? '1 file' : `${elenco.length} file`}
          </span>
        )}
      </h3>

      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      {inCaricamento ? (
        <p className="aiuto">Caricamento degli allegati…</p>
      ) : elenco === null || elenco.length === 0 ? (
        <p className="aiuto blocco-nota">Nessun allegato su questa scadenza.</p>
      ) : (
        <ul className="elenco-allegati">
          {elenco.map((a) => (
            <li key={a.id}>
              {/*
                Un link normale e non un pulsante con `fetch`: il browser sa
                aprire un PDF o un'immagine molto meglio di quanto potremmo
                fare noi, e il cookie di sessione viaggia con la navigazione.
                `rel="noreferrer"` perché la scheda nuova non ha motivo di
                sapere da dove arriva.
              */}
              <a
                href={apiAllegati.urlDi(a.id)}
                target="_blank"
                rel="noreferrer"
                className="allegato-nome"
              >
                {a.nomeFile}
              </a>

              <span className="allegato-dati">
                {a.dimensioneLeggibile}
                {' · '}
                {/*
                  Chi l'ha caricato può essere stato cancellato: `caricatoDa`
                  va a NULL e resta il file senza il suo autore. Dirlo è più
                  onesto che mostrare uno spazio vuoto.
                */}
                {a.caricatoDaNome ?? 'autore non più registrato'}
                {' · '}
                {istante(a.caricatoIl)}
              </span>

              {/* Il pulsante sparisce quando le impostazioni riservano
                  l'eliminazione agli amministratori: mostrarlo disabilitato
                  direbbe «potresti, ma non ora», che qui è falso. */}
              {regole?.puoEliminare !== false && (
                <button
                  type="button"
                  className="bottone-piatto pericolo"
                  onClick={() => void elimina(a)}
                  disabled={inCancellazione === a.id}
                  // Il nome nell'etichetta: in un elenco di pulsanti tutti
                  // uguali, «Elimina» da solo non dice a un lettore di schermo
                  // quale file sta per sparire.
                  aria-label={`Elimina l'allegato ${a.nomeFile}`}
                >
                  {inCancellazione === a.id ? 'Eliminazione…' : 'Elimina'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pieno ? (
        <p className="aiuto blocco-nota">
          Questa scadenza ha raggiunto il massimo di {regole?.maxPerScadenza} allegati. Per
          aggiungerne un altro elimina prima quello che non serve più.
        </p>
      ) : (
      <div className="allegati-aggiungi">
        <input
          ref={campoFile}
          id={`allegato-${scadenzaId}`}
          type="file"
          // `accept` filtra la finestra di scelta del sistema, e i tipi
          // arrivano dal backend: è un aiuto, non un controllo — si può
          // sempre scegliere "tutti i file", e allora decide il server.
          accept={regole?.tipiAmmessi.join(',')}
          disabled={inInvio}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file !== undefined) void carica(file);
          }}
        />
        {inInvio && <span className="aiuto">Caricamento in corso…</span>}
      </div>
      )}

      {regole !== null && (
        <p className="aiuto">
          {regole.descrizioneTipi}, fino a {regole.massimoLeggibile} per file.
        </p>
      )}
    </section>
  );
}
